/**
 * Loads the frozen question bank (contract/seed_question_bank.json) and exposes
 * typed, indexed access. This is the ONLY module that reads the seed; everything
 * else imports these helpers. Never re-parse the source Excel (CLAUDE.md #1).
 */
import seedJson from '../../../contract/seed_question_bank.json' with { type: 'json' };
import type { QuestionBankSeed, Dimension, Question, DimensionSlug } from './types.ts';

const seed = seedJson as QuestionBankSeed;

export const BANK_VERSION: string = seed.version;
export const SCALE = seed.scale;

/** All 15 dimensions, ordered for presentation. */
export const dimensions: Dimension[] = [...seed.dimensions].sort(
  (a, b) => a.display_order - b.display_order,
);

/** All 61 questions, in survey order. */
export const questions: Question[] = [...seed.questions].sort(
  (a, b) => a.display_order - b.display_order,
);

const dimensionBySlug = new Map<DimensionSlug, Dimension>(
  dimensions.map((d) => [d.slug, d]),
);
const questionByRef = new Map<number, Question>(
  questions.map((q) => [q.external_ref, q]),
);

/** raw label (lowercased) -> canonical slug, for import normalization. */
const aliasToSlug = new Map<string, DimensionSlug>();
for (const d of dimensions) {
  aliasToSlug.set(d.canonical_name.toLowerCase(), d.slug);
  for (const a of d.aliases) aliasToSlug.set(a.toLowerCase(), d.slug);
}

export function getDimension(slug: DimensionSlug): Dimension | undefined {
  return dimensionBySlug.get(slug);
}

export function getQuestion(ref: number): Question | undefined {
  return questionByRef.get(ref);
}

/** Survey sections: questions grouped by PRIMARY dimension, in display order. */
export function surveySections(): { dimension: Dimension; questions: Question[] }[] {
  return dimensions.map((dimension) => ({
    dimension,
    questions: questions.filter((q) => q.primary_dimension === dimension.slug),
  }));
}

/**
 * The dimensions a question contributes to in ADMIN analysis: primary + every secondary.
 * (Survey uses only the primary; analysis uses this.)
 */
export function analysisDimensions(q: Question): DimensionSlug[] {
  return [q.primary_dimension, ...q.secondary_dimensions];
}

/** For a given dimension, the questions whose primary OR secondary is that dimension. */
export function questionsForDimension(slug: DimensionSlug): Question[] {
  return questions.filter(
    (q) => q.primary_dimension === slug || q.secondary_dimensions.includes(slug),
  );
}

/** Resolve a free-text dimension label (Excel/import) to a canonical slug, or undefined. */
export function resolveDimensionSlug(rawLabel: string): DimensionSlug | undefined {
  return aliasToSlug.get(rawLabel.trim().toLowerCase());
}
